import { useEffect, useRef, useState } from 'react'
import { signal, computed, useSignalEffect } from '@preact/signals-react'
import { doQuery } from '../utils/useQuery'
import OutTemplate from '@client/models/outTemplate'
import InDataList from '@client/models/inDataList'
import ConverterList from '@client/models/converter'
import ProjectModel, { TProject } from '../models/project'

const outTemplate = new OutTemplate()
const inDataList = new InDataList()
const converterList = new ConverterList()
const projectModel = new ProjectModel()

// export type TProject = {
//     id: string
//     name: string
//     createdAt?: string
//     updatedAt?: string
//     [key: string]: any
// }

const templates = signal<any[] | null>(null)
const dataLists = signal<any[] | null>(null)
const converterLists = signal<any[] | null>(null)

const currentProject = signal<TProject | null>(null)

const isLoaded = computed(() => currentProject.value !== null)

async function loadProject(projectOrId: TProject | string | null) {
    if (!projectOrId) return null
    clearProject()
    return new Promise(async (resolve) => {
        if (typeof projectOrId !== 'string') {
            currentProject.value = projectOrId as any
            resolve(currentProject.value)
        } else {
            projectModel
                .read(projectOrId as any)
                .then((result) => {
                    currentProject.value = result?.data
                    resolve(currentProject.value)
                })
                .catch((err) => {
                    console.error(err)
                    resolve(null)
                })
        }
    })
}

function updateProject(updates: Partial<TProject>) {
    if (currentProject.value) {
        currentProject.value = {
            ...currentProject.value,
            ...updates,
        }
    }
}

function clearProject() {
    currentProject.value = null

    // clear cached project-scoped lists so sidebars return to initial/loading state cleanly
    templates.value = null
    dataLists.value = null
    converterLists.value = null
}

function useTemplates() {
    const error = false
    const [isLoading, setIsLoading] = useState<boolean>(true)

    useSignalEffect(() => {
        const _waitForData = async () => {
            setIsLoading(true)
            try {
                const res = (await doQuery(outTemplate.list, {
                    project: currentProject.value?._id,
                    page: 1,
                    count: 200,
                })) as any
                templates.value = res?.data || []
            } catch (err: any) {
                if (err.response) {
                    console.log('data:', err.response.data)
                    console.log('status:', err.response.status)
                    console.log('headers:', err.response.headers)
                } else {
                    console.log('Error:', err.message)
                }
            } finally {
                setIsLoading(false)
            }
        }
        if (currentProject.value && templates.value === null) {
            _waitForData()
        }
    })

    return {
        loading: isLoading,
        data: templates.value,
        error: error,
    }
}

// Loads of the list run side by side: opening the sidebar starts two (React's
// StrictMode in development), and an upload empties the list at its start and
// again when its import is done. Two rules (operator, 14.10.2026):
// - an older result never replaces a newer one: a result counts only if its
//   load was started after the one shown. An answer from before the import
//   used to arrive last and win;
// - every part of the screen follows the list, not only its own load. 0.15.3
//   dropped the result of all but the latest-started load — and the sidebar,
//   whose own load was dropped, drew the empty list and never learned that the
//   other one had filled it.
let dataListsStarted = 0
let dataListsShown = 0

function useDataLists() {
    const error = false
    const [isLoading, setIsLoading] = useState<boolean>(true)
    // drawn again whenever the list changes, whoever loaded it
    const [data, setData] = useState<any[] | null>(dataLists.value)
    useSignalEffect(() => {
        setData(dataLists.value)
    })

    useSignalEffect(() => {
        const _waitForData = async () => {
            setIsLoading(true)
            const load = ++dataListsStarted
            try {
                const res = await doQuery(inDataList.list, {
                    project: currentProject.value?._id,
                    page: 1,
                    count: 200
                })
                if (load < dataListsShown) return
                dataListsShown = load
                dataLists.value = res?.data || []
            } catch (err: any) {
                if (err.response) {
                    console.log('data:', err.response.data)
                    console.log('status:', err.response.status)
                    console.log('headers:', err.response.headers)
                } else {
                    console.log('Error:', err.message)
                }
            } finally {
                setIsLoading(false)
            }
        }
        if (currentProject.value && dataLists.value == null) {
            _waitForData()
        }
    })

    return {
        loading: isLoading,
        data,
        error: error,
    }
}

function useConverterLists() {
    const error = false
    const [isLoading, setIsLoading] = useState<boolean>(true)

    useSignalEffect(() => {
        const _waitForData = async () => {
            setIsLoading(true)
            try {
                const res = await doQuery(converterList.list, {
                    project: currentProject.value?._id,
                    page: 1,
                    count: 200,
                })
                converterLists.value = res?.data || []
            } catch (err: any) {
                if (err.response) {
                    console.log('data:', err.response.data)
                    console.log('status:', err.response.status)
                    console.log('headers:', err.response.headers)
                } else {
                    console.log('Error:', err.message)
                }
            } finally {
                setIsLoading(false)
            }
        }
        if (currentProject.value && converterLists.value == null) {
            _waitForData()
        }
    })

    return {
        loading: isLoading,
        data: converterLists.value,
        error: error,
    }
}

export default {
    templates,
    dataLists,
    converterLists,
    currentProject,
    isLoaded,
    loadProject,
    updateProject,
    clearProject,
    useTemplates,
    useDataLists,
    useConverterLists,
}
